import {
	ROAD_AI_MAX_PICK_POINTS,
	ROAD_AI_MAX_WAYPOINTS,
	type RoadAiBlockedReason,
	type RoadAiDataSelection,
	type RoadAiRunErrorCode,
} from "./roadAi.types";

/** Point pick guidance appended before picking (D3D-852). */
const POINT_PICK_GUIDE = [
	`ビュー上で始点 → 中継点 → 終点の順にクリックしてください（中継点は最大${ROAD_AI_MAX_WAYPOINTS}点、全体で最大${ROAD_AI_MAX_PICK_POINTS}点）。`,
	"最後にクリックした点が終点になります。右クリックして「完了」を選ぶと確定します。",
].join("\n");

/**
 * 使用データ確認メッセージ（AC-3 / AC-36）。
 * 始終点選択の前と実行直前の 2 回提示する。同意応答は求めない。
 */
export const buildDataSelectionMessage = (
	selection: RoadAiDataSelection,
	occasion: "before_pick" | "before_run",
): string => {
	const header =
		occasion === "before_pick"
			? "次のデータを使用して仮設道路を検討します。"
			: "次のデータで経路生成を開始します。";
	const design =
		selection.mode === "standard"
			? `設計データ: ${selection.design.name}`
			: "設計データは使用しません（点群のみで検討します）。";
	const footer =
		occasion === "before_run"
			? "使用データを変更したい場合は、表示するデータを変更したうえで最初からやり直してください。"
			: POINT_PICK_GUIDE;
	return [header, `点群データ: ${selection.pointCloud.name}`, design, footer]
		.filter((line) => line !== "")
		.join("\n");
};

/** 開始不可のガイド（AC-2）。どれを使うかは選ばせず、表示を絞るよう促す。 */
export const buildBlockedGuideMessage = (
	reason: RoadAiBlockedReason,
): string => {
	switch (reason) {
		case "POINT_CLOUD_NONE":
			return "現況の点群データが表示されていません。使用する点群を 1 件だけ表示してから、もう一度指示してください。";
		case "POINT_CLOUD_MULTIPLE":
			return "点群データが複数表示されています。使用する点群 1 件だけを表示した状態にしてから、もう一度指示してください。";
		case "DESIGN_MULTIPLE":
			return "設計データが複数表示されています。使用する設計 1 件だけを表示するか、設計をすべて非表示にしてから、もう一度指示してください。";
		default:
			return "表示中のデータを確認できませんでした。点群 1 件・設計 0 件または 1 件の状態にしてから、もう一度指示してください。";
	}
};

/** 実行中止・中断の文言（AC-11）。原因と次に取るべき操作を 1 メッセージに含める。 */
export const buildRunErrorMessage = (code: RoadAiRunErrorCode): string => {
	switch (code) {
		case "POINT_CLOUD_NOT_FOUND":
			return "表示中の点群データが現場に見つからないため、経路生成を中止しました。点群の表示を確認してから、もう一度お試しください。";
		case "HEIGHT_MAP_UNAVAILABLE":
			return "現況データ（heightmap）を取得できないため、経路生成を中止しました。点群の処理が完了しているかを確認してから、もう一度お試しください。";
		case "DESIGN_NOT_FOUND":
			return "表示中の設計データが現場に見つからないため、経路生成を中止しました。設計の表示を確認してから、もう一度お試しください。";
		case "DESIGN_ORG_MESH_NOT_REGISTERED":
			return "表示中の設計データに変換前の設計面が登録されていないため、経路生成を中止しました。設計データを登録し直すか、設計を非表示にして点群のみで検討してください。";
		case "DESIGN_MESH_UNAVAILABLE":
			return "設計面データを取得できないため、経路生成を中止しました。時間をおいてからもう一度お試しいただくか、設計を非表示にして点群のみで検討してください。";
		case "INVALID_OBJECTIVE_FOR_MODE":
			return "点群のみで検討する場合は、掘りすぎ回避・盛りすぎ回避を目的に指定できません。切土を減らす・盛土を減らす・切盛のバランスを取る、から目的を指定してください。";
		case "INVALID_REQUEST":
			return `指定内容に誤りがあるため、経路生成を開始できませんでした。始点・終点・中継点（最大 ${ROAD_AI_MAX_WAYPOINTS} 点）や道路幅・勾配の指定を見直して、もう一度お試しください。`;
		case "ROUTE_ENGINE_BUSY":
			return "他の経路生成が実行中のため、開始できませんでした。しばらく待ってからもう一度お試しください。";
		case "ROUTE_ENGINE_UNAVAILABLE":
			return "経路生成サービスが応答していないため、実行を開始できませんでした。時間をおいてからもう一度お試しください。";
		case "JOB_NOT_FOUND":
			return "経路生成の実行状況を確認できなかったため、結果を取得できませんでした。お手数ですが、もう一度実行してください。";
		case "SOURCE_CACHE_EXPIRED":
			return "使用データの保持期限が切れたため、経路生成を完了できませんでした。もう一度実行すると解消します。";
		case "ROUTE_ENGINE_INTERRUPTED":
			return "経路生成の処理が中断されたため、結果を取得できませんでした。お手数ですが、もう一度実行してください。";
		case "ROUTE_ENGINE_FAILED":
			return "経路生成に失敗しました。始点・終点や条件を見直したうえで、もう一度お試しください。";
		case "NETWORK_ERROR":
			return "通信に失敗したため、経路生成の結果を取得できませんでした。ネットワークの状態を確認してから、もう一度お試しください。";
		default:
			return "経路生成を完了できませんでした。もう一度お試しください。";
	}
};

/** 候補が 0 件だったときの見直し案内（AC-37）。 */
export const buildNoCandidateMessage = (reason?: string): string => {
	const detail = reason ? `（理由: ${reason}）` : "";
	return `条件を満たす経路が見つかりませんでした${detail}。始点・終点の位置を見直して、もう一度指示してください。`;
};
